/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import dayjs, { type Dayjs } from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { DATE_FORMAT } from '@/constants';

// Module autonome : propre `dayjs.extend`, ne pas dépendre d'un ordre d'import
// fortuit (même piège que `validationInformationsNaissance`/`formatters`).
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(customParseFormat);

/**
 * Règles croisées « date de reconnaissance / date de naissance de l'enfant »,
 * selon le sous-type d'acte (`codeSousTypeActe`, dérivé de `tr_codj`).
 *
 * ⚠️ **Proposition, à ajuster** : ces règles ne sont pas sourcées d'un
 * document métier, mais déduites de la sémantique des libellés du
 * référentiel `ec_typrec` (`N` = Avant la naissance, `V` = Pendant la vie,
 * `D` = Après le décès). Isolées dans cette fonction pour être facilement
 * corrigées sans toucher au reste des validations.
 *
 * - `N` (avant la naissance) : la reconnaissance doit précéder la naissance,
 *   si celle-ci est connue (elle ne l'est pas nécessairement, décision 8).
 * - `V` (pendant la vie) : la reconnaissance doit suivre la naissance ; la
 *   date de naissance est obligatoire pour ce sous-type (elle n'est rendue
 *   optionnelle que pour `N`), le contrôle s'applique donc systématiquement.
 * - `D` (après le décès) : aucun contrôle croisé (l'enfant est déjà décédé,
 *   la comparaison à sa date de naissance n'apporte rien).
 */
export const validationCroiseeParSousType = (
  codeSousTypeActe: string | undefined | null,
  dateReconnaissance: Dayjs,
  dateNaissance: Dayjs | null,
): Record<string, string> => {
  if (
    !dateReconnaissance.isValid() ||
    _.isNil(dateNaissance) ||
    !dateNaissance.isValid()
  ) {
    return {};
  }

  switch (codeSousTypeActe) {
    case 'N':
      if (dateReconnaissance.isAfter(dateNaissance, 'day')) {
        return {
          dateReconnaissance:
            "La reconnaissance « avant la naissance » doit précéder la date de naissance de l'enfant",
        };
      }
      return {};

    case 'V':
      if (dateReconnaissance.isBefore(dateNaissance, 'day')) {
        return {
          dateReconnaissance:
            "La reconnaissance « pendant la vie » doit suivre la date de naissance de l'enfant",
        };
      }
      return {};

    case 'D':
    default:
      return {};
  }
};

/** Reconstruit la date de naissance de l'enfant (3 champs à plat) en `Dayjs`, ou `null` si non renseignée. */
const buildDateNaissanceEnfant = (data: Record<string, any>): Dayjs | null => {
  const { anneeNaissance, moisNaissance, jourNaissance } = data ?? {};
  if (
    _.isNil(anneeNaissance) ||
    _.isNil(moisNaissance) ||
    _.isNil(jourNaissance)
  ) {
    return null;
  }
  return dayjs(
    `${anneeNaissance}-${moisNaissance}-${jourNaissance}`,
    'YYYY-M-D',
  );
};

/**
 * Valide la section « INFORMATIONS SUR L'EVENEMENT » (reconnaissance).
 *
 * Obligatoires : auteur de la reconnaissance, date de reconnaissance, lieu de
 * reconnaissance (commune). Cohérence heure/minute. La date doit être valide,
 * non future, et antérieure ou égale à la date de l'acte ; des règles
 * croisées supplémentaires s'appliquent selon le sous-type (cf.
 * `validationCroiseeParSousType`).
 *
 * Les clés correspondent aux `name` des champs de `InformationsEvenement`.
 */
const validationInformationsEvenementReconnaissance = (
  data: Record<string, any>,
) => {
  const errors = {
    auteurReconnaissance: '',
    dateReconnaissance: '',
    heureReconnaissance: '',
    minuteReconnaissance: '',
    adresseReconnaissance: '',
  };

  if (_.isNil(data?.auteurReconnaissance) || data.auteurReconnaissance === '') {
    errors.auteurReconnaissance =
      "L'auteur de la reconnaissance est obligatoire";
  }

  const dateReconnaissance = dayjs(data?.dateReconnaissance, DATE_FORMAT, true);

  if (_.isEmpty(data?.dateReconnaissance)) {
    errors.dateReconnaissance = 'La date de reconnaissance est obligatoire';
  } else if (!dateReconnaissance.isValid()) {
    errors.dateReconnaissance = 'La date de reconnaissance est invalide';
  } else if (dateReconnaissance.isAfter(dayjs(), 'day')) {
    errors.dateReconnaissance =
      "La date de reconnaissance ne peut pas être postérieure à aujourd'hui";
  } else {
    if (!_.isEmpty(data?.dateActe)) {
      const dateActe = dayjs(data.dateActe, DATE_FORMAT, true);
      if (dateActe.isValid() && dateReconnaissance.isAfter(dateActe, 'day')) {
        errors.dateReconnaissance =
          "La date de reconnaissance doit être antérieure ou égale à la date de l'acte";
      }
    }

    if (!errors.dateReconnaissance) {
      const dateNaissanceEnfant = buildDateNaissanceEnfant(data);
      const erreursCroisees = validationCroiseeParSousType(
        data?.codeSousTypeActe,
        dateReconnaissance,
        dateNaissanceEnfant,
      );
      Object.assign(errors, erreursCroisees);
    }
  }

  if (
    !_.isNil(data?.heureReconnaissance) &&
    _.isNil(data?.minuteReconnaissance)
  ) {
    errors.minuteReconnaissance = 'Les minutes sont obligatoires';
  }

  if (
    !_.isNil(data?.minuteReconnaissance) &&
    _.isNil(data?.heureReconnaissance)
  ) {
    errors.heureReconnaissance = "L'heure est obligatoire";
  }

  if (_.isEmpty(data?.adresseReconnaissance?.libelleCommune)) {
    errors.adresseReconnaissance = 'Le lieu de reconnaissance est obligatoire';
  }

  return _.pickBy(errors, (value) => value !== '');
};

export default validationInformationsEvenementReconnaissance;
